import React, { ChangeEventHandler, useEffect, useState } from 'react'
import {
  Dialog,
  Stack,
  Autocomplete,
  TextField,
  Grid,
  Button,
  FormControlLabel,
  Checkbox,
  Alert,
} from '@mui/material'
import {
  addSite,
  getAllBestGaugeOnwers,
  getAllCatchments,
  getAllGaugeStations,
  getAllRegions,
  getAllWaterways,
  getAllZones,
  getSite,
  updateSite,
} from '../../../model'
import { containsWhitespace } from '../../../utils'
import { SiteViewModel } from '../../ViewModel'

interface NewSiteDialogProps {
  siteId?: string
  open: boolean
  onClose: () => void
}

const initialsSite: SiteViewModel = {
  siteName: '',
  id: '',
  siteType: '',
  catchment: '',
  waterway: '',
  region: '',
  zone: '',
  publicAccess: true,
  notes: '',
  latitude: '',
  longitude: '',
  bestGaugeOwner: '',
  gaugeStation: '',
}

export default function NewSiteDialog({
  siteId,
  open,
  onClose,
}: NewSiteDialogProps) {
  const zoneOptions = getAllZones().sort()
  const catchments = getAllCatchments().sort()
  const regions = getAllRegions()
  const waterways = getAllWaterways().sort()
  const gaugeOwners = getAllBestGaugeOnwers().sort()
  const gaugeStations = getAllGaugeStations().sort()

  const [site, setSite] = useState<SiteViewModel>(initialsSite)

  const [loading, setLoading] = useState(false)
  const [error, setError] = useState({ value: false, message: '' })

  const isValidId = siteId && siteId.trim().length !== 0

  useEffect(() => {
    console.log('New Site: ' + siteId + '---' + isValidId)

    if (isValidId) {
      const fetchedsite = getSite(siteId)

      setSite(fetchedsite)
    }
    return () => {
      setSite(initialsSite)

      setLoading(false)
      setError({ value: false, message: '' })
    }
  }, [open])

  const handleSubmit = async () => {
    console.log('handle submit')
    try {
      setError({ value: false, message: '' })
      setLoading(true)
      const submittedSite: SiteViewModel = {
        ...site,
        siteType: 'surface water',
        notes: '',
      }

      if (isValidId) {
        await updateSite(submittedSite)
      } else {
        await addSite(submittedSite)
      }

      onClose()
    } catch (e) {
      setError({ value: true, message: typeof e === 'string' ? e : e.message })
    } finally {
      setLoading(false)
    }
  }

  /**
   * @param id input id corresponding to SiteViewModel key
   *
   * !!!!Warning: `id` must be the same as SiteViewModel key. This will affect database schema.
   */
  const handleChange = (id: string, value: string) => {
    const keys = Object.keys(initialsSite)
    // Prevent mistyping id
    if (!keys.includes(id)) {
      console.log('No idea: ' + id)
      setError({
        value: true,
        message: 'NewSiteDialog: Failed to add site. Please contact admin.',
      })
      return
    }

    if (id === 'id') {
      setError({
        value: containsWhitespace(value) ? true : false,
        message: containsWhitespace(value)
          ? "Site code can't have white space!"
          : '',
      })
      setSite((site) => ({ ...site, [id]: value.toUpperCase() }))
      return
    }

    setSite((site) => ({ ...site, [id]: value }))
  }

  return (
    <Dialog open={open} fullWidth={true} maxWidth={'lg'} onClose={onClose}>
      <Stack p={2} spacing={2}>
        <TextField
          label="Site Name"
          required
          variant="standard"
          value={site.siteName}
          onChange={(e) => handleChange('siteName', e.target.value)}
        />
        <TextField
          label="Site Code"
          disabled={isValidId}
          required
          variant="standard"
          value={site.id}
          onChange={(e) => handleChange('id', e.target.value)}
        />
        <FormControlLabel
          control={
            <Checkbox
              id="publicAccess"
              checked={!site.publicAccess}
              onChange={(e) => {
                setSite((site) => ({
                  ...site,
                  publicAccess: !e.target.checked,
                }))
              }}
            />
          }
          label="Private"
        />

        <Grid container rowGap={2} columnSpacing={1}>
          <Grid item xs={12} md={6}>
            <SiteAutoTextField
              label="Region"
              value={site.region}
              required
              options={regions}
              onChangeText={(t) => handleChange('region', t)}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <SiteAutoTextField
              label="Zone"
              value={site.zone}
              required
              options={zoneOptions}
              onChangeText={(t) => handleChange('zone', t)}
            />
          </Grid>
        </Grid>

        <Grid container rowGap={2} columnSpacing={1}>
          <Grid item xs={12} md={6}>
            <SiteAutoTextField
              label="Catchment"
              value={site.catchment}
              required
              options={catchments}
              onChangeText={(t) => handleChange('catchment', t)}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <SiteAutoTextField
              label="Water way"
              value={site.waterway}
              options={waterways}
              onChangeText={(t) => handleChange('waterway', t)}
            />
          </Grid>
        </Grid>

        <Grid container rowGap={2} columnSpacing={1}>
          <Grid item xs={12} md={6}>
            <SiteAutoTextField
              label="Best Gauge Owner"
              value={site.bestGaugeOwner}
              required
              options={gaugeOwners}
              onChangeText={(t) => handleChange('bestGaugeOwner', t)}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <SiteAutoTextField
              label="Gauge Station"
              value={site.gaugeStation}
              required
              options={gaugeStations}
              onChangeText={(t) => handleChange('gaugeStation', t)}
            />
          </Grid>
        </Grid>

        <Grid container rowGap={2} columnSpacing={1}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              required
              label="Latitude"
              variant="standard"
              value={site.latitude}
              onChange={(e) => handleChange('latitude', e.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              required
              label="Longtitude"
              variant="standard"
              value={site.longitude}
              onChange={(e) => handleChange('longitude', e.target.value)}
            />
          </Grid>
        </Grid>

        <Button
          type="submit"
          variant="contained"
          disabled={loading}
          onClick={handleSubmit}
        >
          {isValidId ? 'Update' : 'Add Site'}
        </Button>
        {error.value && (
          <Alert severity="error">
            {error.message ?? 'Unable to add site! Please try again.'}
          </Alert>
        )}
      </Stack>
    </Dialog>
  )
}

function SiteAutoTextField({
  label,
  required,
  options,
  value,
  onChangeText,
}: {
  label: string
  required?: boolean
  options: ReadonlyArray<string>
  value: string
  onChangeText: (text: string) => void
}) {
  const [textValue, setTextValue] = useState<string | null>(null)
  const [inputValue, setInputValue] = useState('')

  return (
    <Autocomplete
      ListboxProps={{ style: { maxHeight: '10rem' } }}
      freeSolo={true}
      selectOnFocus
      options={options}
      renderInput={(params) => (
        <TextField
          {...params}
          required={required ?? false}
          label={label}
          variant="standard"
        />
      )}
      value={value}
      onChange={(_, value) => {
        setTextValue(value)
      }}
      inputValue={inputValue}
      onInputChange={(_, value) => {
        setInputValue(value)
        onChangeText(value)
      }}
    />
  )
}
